import React, {
  useState,
  useEffect,
  useContext,
  createContext,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { useLazyQuery, useMutation } from '@apollo/client';
import { LOGIN_PASSCODE, REG_USER_CHECKIN } from '../../../network/mutations';
import { RootContext } from '../../../layouts/RootLayout/context';
import { GET_EVENT_USER } from '../../../network/queries';

export const Context = createContext({});
export const Provider = ({ children }) => {
  const { virtualPhoneNumber } = useParams();
  const { authorize, isAuthorized } = useContext(RootContext);
  const [validationError, setValidationError] = useState([]);
  const history = useHistory();

  const schema = yup.object().shape({
    passcode: yup.string().required('Oops! CVC code can`t be blank'),
  });

  const [fields, setFields] = useState({
    passcode: '',
  });

  useEffect(() => {
    authorize(null, virtualPhoneNumber);
  }, []);

  const goToIndex = () => {
    history.push(`/in/${virtualPhoneNumber}`);
  };

  const [
    loginWithPasscode,
    {
      loading: loginLoading,
      error: loginError,
    },
  ] = useMutation(LOGIN_PASSCODE);

  const [
    checkinUser,
    {
      loading: checkinLoading,
      error: checkinError,
    },
  ] = useMutation(REG_USER_CHECKIN);

  const [getEventUser, {
    loading: eventUserLoading,
    error: eventUserError,
    data: eventUserData,
  }] = useLazyQuery(GET_EVENT_USER);

  // ====================================
  // Validation

  const handleFormValidation = (formFields) => {
    schema.validate(formFields, { abortEarly: false }).then(
      () => {
        setValidationError([]);
      },
      (response) => {
        setValidationError(response.inner);
      },
    );
  };

  useEffect(() => {
    handleFormValidation({ ...fields });
  }, [fields]);

  // ====================================
  // General Actions

  const loadUserInfo = (registrationId) => {
    getEventUser({
      variables: {
        registrationId,
      },
    });
  };

  const handleCheckin = (registrationId, { onSuccess, onError }) => {
    const inputData = {
      registrationId,
    };

    checkinUser({ variables: { input: inputData } }).then(
      (result) => {
        if (typeof onSuccess === 'function') {
          onSuccess(result);
        } else {
          console.log(result);
        }
      },
      (error) => {
        if (typeof onError === 'function') {
          onError(error);
        } else {
          console.log(error);
        }
      },
    );
  };

  const handleUpdateValue = (name, value) => {
    setFields({ ...fields, [name]: value });
  };

  const handleAuth = ({ onSuccess, onError }) => {
    const { passcode } = fields;

    const inputData = {
      passcode,
      vpn: virtualPhoneNumber,
    };

    loginWithPasscode({ variables: { input: inputData } }).then(
      (result) => {
        const { data: { loginWithPasscode: loginData } } = result;

        authorize(loginData, virtualPhoneNumber);

        if (typeof onSuccess === 'function') {
          onSuccess(inputData, result);
        } else {
          console.log(result);
        }
      },
      (error) => {
        if (typeof onError === 'function') {
          onError(error);
        } else {
          console.log(error);
        }
      },
    );
  };

  const contextData = {
    formData: fields,
    setField: handleUpdateValue,
    login: handleAuth,
    isFormValid: validationError.length === 0,
    isLoggedIn: isAuthorized,
    onCheckin: () => false,

    loginLoading,
    loginError,

    checkinLoading,
    checkinError,
    handleCheckin,

    loadUserInfo,
    eventUserLoading,
    eventUserError,
    eventUserData,

    goToIndex,
  };

  return <Context.Provider value={contextData}>{children}</Context.Provider>;
};

export const { Consumer } = Context;

Provider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
